import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as BookingService from '_services/booking-service';
import * as BookingAnalyticsService from '_services/bookingAnalytics-service';
import { format } from 'date-fns';

const initialState = {
  selectedView: 1,
  bookings: null,
  filtered: null,
  postedBooking: null,
  airlineSelectedAirport: 'DUB',
  status: 'idle',
  postStatus: 'idle',
  patchBookingStatus: 'idle',
  deleteBookingStatus: 'idle',
  error: null,
  postError: null,
  confirmError: null,
  bookingList: [],
  bookingListStatus: 'idle',
  postBookingListError: null,
  additionalPassengers: [{
    firstName: "",
    secondName: "",
    assistance: [],
    locationInfo: {}
  }],

};

// get all bookings
export const getBookings = createAsyncThunk('bookings/getBookings', async (params) => {
  return BookingService.getBookings(params);
});

// post a booking
export const postBooking = createAsyncThunk('bookings/postBooking', async (booking) => {
  return BookingService.postBooking(booking);
});

// post bookingsList
export const postBookingListBooking = createAsyncThunk('bookings/postBookingListBooking', async (bookingList) => {
  //console.log('posting booking list in slice...')
  return BookingService.postBookings(bookingList);
});

// patch booking
export const patchBooking = createAsyncThunk('bookings/confirmBooking', async (booking) => {
  return BookingService.patchBooking(booking);
});

// patch booking list
export const patchBookingList = createAsyncThunk('bookings/patchBookingList', async (booking) => {
  return BookingService.patchBookings(booking);
});

// get month bookings 
export const getMonthCount = createAsyncThunk('bookings/getMonthCount', async (params) => {
  return BookingAnalyticsService.getBookingsCount(params);
});

// delete booking
export const deleteBooking = createAsyncThunk('bookings/deleteBooking', async (booking, editBooking) => {
  return BookingService.deleteBooking(booking, editBooking);
});

// delete multiple bookings
export const deleteBookings = createAsyncThunk('bookings/deleteBookings', async (bookings) => {
  return BookingService.deleteBookings(bookings);
});

// update booking, send only properties you need to update
export const updateBooking = createAsyncThunk('bookings/updateBooking', async (booking) => {
  return BookingService.updateBooking(booking);
});

const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    reset: (state) => {
      return initialState
    },
    setAirlineSelectedAirport: (state, action) => {
      return {
        ...state,
        airlineSelectedAirport: action.payload
      }
    },

    filter: (state, action) => {
      return {
        ...state,
        filtered: state.bookings.filter((b) => {
          let bookingDate = new Date(b.flightConfirmationDetails.departureDate),
            selectedDate = action.payload;
          return bookingDate.getFullYear() === selectedDate.getFullYear() &&
            bookingDate.getMonth() === selectedDate.getMonth() &&
            bookingDate.getDate() === selectedDate.getDate();
        }),
      }
    },
    resetPostedBooking: (state) => {
      return {
        ...state,
        postedBooking: null,
        postStatus: 'idle',
        deleteBookingStatus: 'idle',
        postError: null,
        confirmError: null,
        patchBookingStatus: 'idle',

      }
    },
    setSelectedView: (state, action) => {
      return {
        ...state,
        selectedView: action.payload
      }
    },
    resetPostedBookingListStatus: (state) => {
      return {
        ...state,
        postStatus: 'idle',
        deleteBookingStatus: 'idle',
        postError: null,
        confirmError: null,
        patchBookingStatus: 'idle',
        bookingListStatus: 'idle',
        bookingList: [],
        postBookingListError: null,
      }
    },
    clearBookingModalFlags: (state) => {
      state.postStatus = 'idle';
      state.postedBooking = null;
      state.postError = null;
    },
    setAdditionalPassengers: (state, action) => {

      return {
        ...state,
        additionalPassengers: action.payload
      }
    },
    resetAdditionalPassengers: (state) => {
      return {
        ...state,
        bookingListStatus: 'idle',
        bookingList: [],
        postBookingListError: null,
        additionalPassengers: [{
          firstName: "",
          secondName: "",
          assistance: [],
          locationInfo: {}
        }]
      }
    }


  },
  extraReducers: {
    [getBookings.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
      }
    },
    [getBookings.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        bookings: action.payload,
      }
    },
    [getBookings.rejected]: (state, action) => {
      return {
        ...state,
        status: 'failed',
        error: action.error.message,
      }
    },
    [postBooking.pending]: (state, action) => {
      return {
        ...state,
        postStatus: 'loading'
      }
    },
    [postBooking.fulfilled]: (state, action) => {
      return {
        ...state,
        postStatus: 'succeeded',
        postedBooking: action.payload,
      }
    },
    [postBooking.rejected]: (state, action) => {
      return {
        ...state,
        postStatus: 'failed',
        postError: action.error.message,
      }
    },
    [patchBooking.pending]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'loading'
      }
    },
    [patchBooking.fulfilled]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'succeeded',
        postedBooking: action.payload,
      }
    },
    [patchBooking.rejected]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'failed',
        confirmError: action.error.message,
      }
    },
    [deleteBooking.pending]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'loading'
      }
    },
    [deleteBooking.fulfilled]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'succeeded'
      }
    },
    [deleteBooking.rejected]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'failed'
      }
    },
    [deleteBookings.pending]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'loading'
      }
    },
    [deleteBookings.fulfilled]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'succeeded'
      }
    },
    [deleteBookings.rejected]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'failed'
      }
    },
    [getMonthCount.pending]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'loading'
      }
    },
    [getMonthCount.fulfilled]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'succeeded'
      }
    },
    [getMonthCount.rejected]: (state, action) => {
      return {
        ...state,
        deleteBookingStatus: 'failed'
      }
    },
    [postBookingListBooking.pending]: (state, action) => {
      return {
        ...state,
        bookingListStatus: 'loading'
      }
    },
    [postBookingListBooking.fulfilled]: (state, action) => {
      return {
        ...state,
        bookingListStatus: 'succeeded',
        bookingList: action.payload,
      }
    },
    [postBookingListBooking.rejected]: (state, action) => {

      return {
        ...state,
        bookingListStatus: 'failed',
        postBookingListError: action.error.message,
      }
    },
    [patchBookingList.pending]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'loading'
      }
    },
    [patchBookingList.fulfilled]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'succeeded',
        bookingList: action.payload,
      }
    },
    [patchBookingList.rejected]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'failed',
        confirmError: action.error.message,
      }
    },
    [updateBooking.pending]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'loading'
      }
    },

    [updateBooking.fulfilled]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'succeeded',

      }
    },
    [updateBooking.rejected]: (state, action) => {
      return {
        ...state,
        patchBookingStatus: 'failed',

      }
    },

  }
});


export const selectMonthCount = (month) => (state) => {
  if (state.bookings.bookings == null) { return [] }
  if (state.bookings.bookings.filter == undefined) { return [] }

  let list = [];


  return [];
};

export const selectListObject = (state) => {
  if (state.bookings.bookings == null) { return [] }
  if (state.bookings.bookings.filter == undefined) { return [] }

  let list = [];
  state.bookings.bookings.forEach(booking => {
    let fullName = booking.name.split(' ');

    list.push({
      firstName: fullName[0],
      lastName: fullName[1],
      assistances: booking.assistance,
      flightNumber: booking.flightConfirmationDetails.carrierCode + booking.flightConfirmationDetails.flightNumber,
      flightTime: ' ',
      contact: ' '
    })
  });

  return list;
};

export const selectMonthBookings = (calendarMonth) => (state) => {

  const month = calendarMonth + 1
  let count = Array(32).fill(0);

  const list = {};

  // if no bookings return list of 0's //
  if (state.bookings.bookings == null) { return count }
  if (state.bookings.bookings.filter == undefined) { return count }

  let duplicatesRemoved = state.bookings.bookings;

  // bookings
  duplicatesRemoved.forEach(booking => {

    // get date of each booking
    let date = booking.flightConfirmationDetails.departureDate
    let split = date.split("-")
    let booking_month = split[1];
    let day = split[2];

    // check if booking is in previous, current or next month
    if (parseInt(booking_month) == month || parseInt(booking_month) == month - 1 || parseInt(booking_month) == month + 1) {
      // add value at date booking index
      count[parseInt(day)] = count[parseInt(day)] + 1;


      let key = parseInt(day, 10) + "/" + parseInt(booking_month)

      if (list[key]) {
        list[key] += 1
      }
      else {
        list[key] = 1
      }

    }
  });

  return list;
};

export const selectPredictedCount = (date) => (state) => {
  let count = 0;

  // if no bookings return list of 0's
  if (state.bookings.bookings == null) { return count }
  if (state.bookings.bookings.filter == undefined) { return count }


  const today = format(new Date(), "yyyy-MM-dd")
  let splitter = today.split('-')
  const current_day = splitter[2]
  const current_month = splitter[1];
  const current_year = splitter[0]
  let selected_month = ''
  let selected_day = ''
  let selected_year = ''
  let totalDays = 0
  let daysRemaining = 0
  let previousDays = 0

  // check if selected month happened in past
  if (current_month > date.month || current_year > date.year) {
    // use previous month
    selected_month = date.month
    selected_year = date.year
    totalDays = getDaysInMonth(selected_month, selected_year)
    previousDays = getDaysInMonth(selected_month, selected_year)
    selected_day = totalDays + 1
  }
  else if (current_month == date.month) {
    // use current month
    selected_month = current_month
    selected_day = current_day
    selected_year = current_year
    totalDays = getDaysInMonth(selected_month, selected_year)
    daysRemaining = totalDays - selected_day
    previousDays = (totalDays - daysRemaining - 1)
  }
  else {
    // month is in future
    return 0;
  }

  // count number of bookings before given date
  state.bookings.bookings.forEach(booking => {

    // get date of each booking
    let date = booking.flightConfirmationDetails.departureDate
    let split = date.split("-")
    let booking_month = split[1];
    let day = split[2];

    // check if booking is in current month and is before current date increase count
    if (parseInt(booking_month) == selected_month && parseInt(day) < selected_day) {
      count++;
    }
  });

  // formula
  const sum = (count / previousDays) * totalDays
  return Math.round(sum);
}

export const selectDailyCount = (state) => {

  const current_date = format(new Date(),)
  const selected_month = date.month
  const selected_day = date.day
  const selected_year = date.year

  let count = 0;

  // if no bookings return list of 0's
  if (state.bookings.bookings == null) { return 0 }
  if (state.bookings.bookings.filter == undefined) { return 0 }

  // count number of bookings before given date
  state.bookings.bookings.forEach(booking => {

    // get date of each booking
    let date = booking.flightConfirmationDetails.departureDate
    let split = date.split("-")
    let booking_month = split[1];
    let day = split[2];

    // check if booking is in current month and is before selected date increase count
    if (parseInt(booking_month) == selected_month && parseInt(day) < selected_day) {
      count++;
    }
  });

  // formula
  const totalDays = getDaysInMonth(selected_month, selected_year)
  const daysRemaining = totalDays - selected_day
  const previousDays = (totalDays - daysRemaining - 1)
  const sum = (count / previousDays) * totalDays

  return sum;
}

var getDaysInMonth = function (month, year) {
  // Here January is 1 based
  //Day 0 is the last day in the previous month
  return new Date(year, month, 0).getDate();
  // Here January is 0 based
  // return new Date(year, month+1, 0).getDate();
};

export const { setSelectedView, setAirlineSelectedAirport, reset, filter, resetPostedBooking, resetPostedBookingListStatus, clearBookingModalFlags, setAdditionalPassengers, resetAdditionalPassengers } = bookingsSlice.actions;
export const selectBookings = state => state.bookings.bookings;
export const selectAirlineSelectedAirport = state => state.bookings.airlineSelectedAirport;
export const selectBookingsStatus = state => state.bookings.status;
export const selectPostedBookingStatus = state => state.bookings.postStatus;
export const selectError = state => state.bookings.error;

export const selectPatchBookingStatus = state => state.bookings.patchBookingStatus;
export const selectPostedBooking = state => state.bookings.postedBooking;

// post booking list
export const selectPostedBookingsStatus = state => state.bookings.bookingListStatus;
export const selectPostBookingListError = state => state.bookings.postBookingListError;
export const selectBookingList = state => state.bookings.bookingList;

export const selectDeleteStatus = state => state.bookings.deleteBookingStatus;
export const postError = state => state.bookings.postError;
export const selectConfirmBookingError = state => state.bookings.confirmError;
export const selectSelectedView = state => state.bookings.selectedView;

export const selectAdditionalPassengers = state => state.bookings.additionalPassengers;
export default bookingsSlice.reducer;
